import { useFormContext } from 'form';
import { useCallback } from 'react';

import type { DrawerFormValues } from '../../components/MatchedSiteMaterialItemDrawer/MatchedSiteMaterialItemDrawer';
import { useMatchedSiteMaterialItem } from '../../contexts/MatchedSiteMaterialItemContext';
import { useNetTakeRateCalculator } from '../../hooks/useNetTakeRateCalculator/useNetTakeRateCalculator';
import { getInitialPernRevenuePerTonne } from '../../utils/getInitialPernRevenuePerTonne/getInitialPernRevenuePerTonne';

export const useTotalNetTakeRateCalculator = () => {
  const { setTotalNetTakeRate, setTotalNetTakeRateLoading, initialSellerOffer, siteMaterial } =
    useMatchedSiteMaterialItem();

  const pernRevenuePerTonne = getInitialPernRevenuePerTonne(initialSellerOffer?.price_details?.pern_revenue_per_tonne);

  const { getValues } = useFormContext<DrawerFormValues>();
  const { calculateNetTakeRate } = useNetTakeRateCalculator({
    onSettled: (ntr, error) => {
      if (error) {
        setTotalNetTakeRate({
          error,
          isLoading: false,
          rate: undefined,
        });
        return;
      }
      if (ntr) {
        setTotalNetTakeRate({
          error: null,
          isLoading: false,
          rate: Number(ntr.net_take_rate),
        });
        return;
      }
      setTotalNetTakeRate({
        error: null,
        isLoading: false,
        rate: undefined,
      });
    },
  });

  const calculateTotalNetTakeRate = useCallback(async () => {
    const data = getValues();
    setTotalNetTakeRateLoading(true);
    await calculateNetTakeRate({
      buyerCurrency: data.buyer_currency,
      buyerPricePerTonne: data.buyer_price_per_tonne,
      materialId: siteMaterial.material_spec.material.id,
      numberOfLoads: data.number_of_loads,
      pernRevenuePerTonne: pernRevenuePerTonne,
      sellerCurrency: data.seller_currency,
      sellerPricePerTonne: data.seller_price_per_tonne,
      tonnesPerLoad: data.tonnes_per_load,
      transportCurrency: data.transport_currency,
      transportPricePerTonne: data.transport_cost,
    });
    setTotalNetTakeRateLoading(false);
  }, [
    getValues,
    setTotalNetTakeRateLoading,
    calculateNetTakeRate,
    siteMaterial.material_spec.material.id,
    pernRevenuePerTonne,
  ]);

  return { calculateTotalNetTakeRate };
};
